import { formatNumber } from 'utils/number';
import type { FieldKey, FieldValue, RowValues, ServerRowResult } from './types';

/** Labels match the server's column headers (BulkAssetBatch::FieldTable). */
export const FIELD_LABELS: Record<FieldKey, string> = {
  instrumentType: 'Interest Type',
  surveyArea: 'Survey Area',
  surveyAreaId: 'Survey Area ID',
  quarterCalls: 'Quarter Call',
  lots: 'Lots',
  tractFullLegalDescription: 'Full Legal Description',
  gridException: 'Grid Exception',
  grossAcres: 'Gross Acres',
  tractNetAcres: 'Net Acres',
  netAcreageUnits: 'Net Acreage Units',
  leaseRoyaltyRate: 'Royalty Rate',
  alternateAssetId: 'Alt Asset ID',
  startDepth: 'Start Depth',
  endDepth: 'End Depth',
  insightsEntity: 'Insights Entity',
  crmAccount: 'CRM Account',
  recordType: 'Record Type',
  leaseRoyaltyType: 'Royalty Type',
  royaltyBurdens: 'Royalty Burdens',
  orriBurdens: 'ORRI Burdens',
  titleQuality: 'Title Quality',
  titleRiskFactor: 'Title Risk Factor',
  tractSource: 'Tract Source',
  depthFullDescription: 'Depth Full Description',
  notes: 'Notes',
};

/** The table's data columns, in order; the rest live only in the tearsheet. */
export const TABLE_FIELDS: FieldKey[] = [
  'instrumentType',
  'surveyArea',
  'quarterCalls',
  'lots',
  'tractFullLegalDescription',
  'gridException',
  'grossAcres',
  'tractNetAcres',
  'netAcreageUnits',
  'leaseRoyaltyRate',
  'alternateAssetId',
  'startDepth',
  'endDepth',
  'crmAccount',
  'recordType',
];

/** Required to add a row; the server checks the same set. */
export const REQUIRED_FIELDS: FieldKey[] = [
  'instrumentType',
  'surveyArea',
  'grossAcres',
  'tractNetAcres',
  'netAcreageUnits',
];

export const isBlank = (value: FieldValue | undefined) =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim() === '') ||
  (Array.isArray(value) && value.length === 0);

export const asText = (value: FieldValue | undefined) => {
  if (Array.isArray(value)) return value.join(', ');
  return value === null || value === undefined ? '' : String(value);
};

/**
 * A list field (Quarter Call) as items, whether typed "NE, SW" or a list.
 * @param {FieldValue | undefined} value - The field's value
 * @return {string[]} The items
 */
export const asList = (value: FieldValue | undefined) => {
  if (Array.isArray(value)) return value;
  return asText(value)
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

/**
 * "Required" for each blank required field, as the tearsheet shows at once.
 * @param {RowValues} values - A row's values
 * @return {object} Field key to "Required"
 */
export const presenceErrors = (values: RowValues) =>
  Object.fromEntries(
    REQUIRED_FIELDS.filter((key) => isBlank(values[key]))
      // A Survey Area ID alone is enough; the server resolves it.
      .filter((key) => key !== 'surveyArea' || isBlank(values.surveyAreaId))
      .map((key) => [key, 'Required'])
  ) as Partial<Record<FieldKey, string>>;

/**
 * "Field: message" lines for the Invalid indicator.
 * @param {object} errors - A row's errors, by field key
 * @return {string[]} One line per field
 */
export const errorLines = (errors: ServerRowResult['errors']) =>
  Object.entries(errors).map(
    ([key, message]) => `${FIELD_LABELS[key as FieldKey] ?? key}: ${message}`
  );

export const EM_DASH = '—';

/**
 * How one value reads in a table cell or in search. Values the server could
 * not parse arrive as typed and show as typed.
 * @param {FieldValue | undefined} value - Its value
 * @return {string} Display text; an em dash for a blank
 */
export const displayValue = (value: FieldValue | undefined) => {
  if (isBlank(value)) return EM_DASH;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'number') {
    return formatNumber({ value, minPrecision: 0, maxPrecision: 10 });
  }
  return String(value);
};
